import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { CompanyPdfHeader } from './shared/CompanyPdfHeader';
import { LabeledPdfRows } from './shared/LabeledPdfBlock';
import { PdfLabeledSections } from './shared/PdfLabeledSections';
import { CartLinesPdfTable } from './shared/CartLinesPdfTable';
import { formatPdfEur } from './shared/formatPdfCurrency';
import { PdfPrintScaleProvider, usePdfStyles } from './shared/PdfPrintScaleContext';

function InvoiceSummaryPdfPage({ data }) {
    const pdfStyles = usePdfStyles();
    const subtitle = [
        data.invoiceNumber ? `Invoice #${data.invoiceNumber}` : null,
        `Order #${data.orderNumber || '—'}`,
    ]
        .filter(Boolean)
        .join(' · ');

    const invoiceRows = [
        { label: 'Order total', value: formatPdfEur(data.invoice?.orderSubtotal) },
        { label: 'Surcharge', value: formatPdfEur(data.invoice?.surcharge) },
        { label: 'Shipping', value: formatPdfEur(data.invoice?.shipping) },
        { label: 'VAT (18%)', value: formatPdfEur(data.invoice?.vat18) },
        { label: 'Combined total', value: formatPdfEur(data.invoice?.combined) },
    ];
    if (data.invoice?.method) {
        invoiceRows.push({ label: 'Method', value: String(data.invoice.method) });
    }

    const billingRows = [
        { label: 'Full name', value: data.billing?.name },
        { label: 'Company', value: data.billing?.company },
        { label: 'VAT no.', value: data.billing?.vat },
        { label: 'Street', value: data.billing?.street },
        { label: 'Town / city', value: data.billing?.city },
        { label: 'Post code', value: data.billing?.postCode },
        { label: 'Phone', value: data.billing?.phone },
        { label: 'Email', value: data.billing?.email },
    ];

    const deliveryRows = [
        { label: 'Full name', value: data.delivery?.name },
        { label: 'Company', value: data.delivery?.company },
        { label: 'VAT no.', value: data.delivery?.vat },
        { label: 'Street', value: data.delivery?.street },
        { label: 'Town / city', value: data.delivery?.city },
        { label: 'Post code', value: data.delivery?.postCode },
        { label: 'Phone', value: data.delivery?.phone },
        { label: 'Email', value: data.delivery?.email },
    ];

    return (
        <Page size="A4" style={pdfStyles.page}>
                <CompanyPdfHeader title="Invoice" subtitle={subtitle} />

                <Text style={pdfStyles.sectionTitle}>Invoice status</Text>
                <LabeledPdfRows
                    rows={[
                        { label: 'Invoice status', value: data.invoiceStatus || data.status },
                        { label: 'Invoice date', value: data.invoiceDate },
                        { label: 'Order date', value: data.orderDate },
                    ]}
                />

                {data.note ? (
                    <View style={{ marginTop: 8 }}>
                        <Text style={pdfStyles.sectionTitle}>Order note</Text>
                        <Text style={{ fontSize: 8 }}>{data.note}</Text>
                    </View>
                ) : null}

                <Text style={pdfStyles.sectionTitle}>Line items</Text>
                <CartLinesPdfTable lines={data.cartLines || []} />

                <PdfLabeledSections
                    sections={[
                        { title: 'Totals', rows: invoiceRows },
                        { title: 'Billing', rows: billingRows, wrap: false },
                        { title: 'Delivery', rows: deliveryRows, wrap: false },
                    ]}
                />
        </Page>
    );
}

/**
 * @param {{ data: Record<string, unknown> & { invoiceStatus?: string }; scale?: number }} props
 */
export function InvoiceSummaryPdfDocument({ data, scale = 1 }) {
    return (
        <Document title={`Invoice ${data.invoiceNumber || data.orderNumber || ''}`}>
            <PdfPrintScaleProvider scale={scale}>
                <InvoiceSummaryPdfPage data={data} />
            </PdfPrintScaleProvider>
        </Document>
    );
}
